import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useVisibility } from "../../contexts/VisibilityContext";
import visibilityService from "../../services/visibilityService";

function VisibilitySettingsPage() {
  const { user } = useAuth();
  const { refresh: refreshMyVisibility } = useVisibility();

  const isAdmin = user?.roles?.some((r) => normalize(r) === "ADMIN");
  const isBerufsbildner = user?.roles?.some(
    (r) => normalize(r) === "BERUFSBILDNER",
  );

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [profession, setProfession] = useState("");
  const [targetRole, setTargetRole] = useState("LEARNER");
  const [draft, setDraft] = useState({});

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const data = await visibilityService.getAll();
        if (!active) return;

        setGroups(data || []);
        if (data?.length > 0) {
          const own = (user?.professionType || "").toString().toUpperCase();
          const match = data.find(
            (g) => (g.professionType || "").toUpperCase() === own,
          );
          setProfession((match || data[0]).professionType);
        }
      } catch (e) {
        if (active) setError("Failed to load visibility settings.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const professions = useMemo(() => {
    const seen = new Map();
    groups.filter(Boolean).forEach((g) => {
      if (!seen.has(g.professionType)) {
        seen.set(g.professionType, g.professionLabel || g.professionType);
      }
    });
    return Array.from(seen.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [groups]);

  const selectedGroup = useMemo(
    () =>
      groups.find(
        (g) =>
          g && g.professionType === profession && g.targetRole === targetRole,
      ),
    [groups, profession, targetRole],
  );

  useEffect(() => {
    const group = groups.find(
      (g) => g.professionType === profession && g.targetRole === targetRole,
    );

    if (group) {
      const next = {};
      group.settings.forEach((s) => {
        next[s.visibilityKey] = s.enabled;
      });
      setDraft(next);
    } else {
      setDraft({});
    }

    setSuccess("");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profession, targetRole]);

  const enabledCount = useMemo(() => {
    if (!selectedGroup) return 0;
    return selectedGroup.settings.filter(
      (s) => draft[s.visibilityKey] !== false,
    ).length;
  }, [selectedGroup, draft]);

  const isOn = (key) => draft[key] === true;
  const hasKey = (key) =>
    !!selectedGroup?.settings?.some((s) => s.visibilityKey === key);

  const toggle = (key) => {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
    setSuccess("");
  };

  const chooseExclusive = (onKey, offKey) => {
    setDraft((prev) => ({ ...prev, [onKey]: true, [offKey]: false }));
    setSuccess("");
  };

  const setAll = (enabled) => {
    if (!selectedGroup) return;
    const next = {};
    selectedGroup.settings.forEach((s) => {
      next[s.visibilityKey] = enabled;
    });
    setDraft(next);
    setSuccess("");
  };

  const handleSave = async ({ auto = false } = {}) => {
    if (!selectedGroup) return;

    setSaving(true);
    setError("");
    if (!auto) setSuccess("");

    try {
      const settings = selectedGroup.settings.map((s) => ({
        visibilityKey: s.visibilityKey,
        label: s.label,
        enabled: draft[s.visibilityKey] !== false,
      }));

      const updated = await visibilityService.updateGroup(
        profession,
        targetRole,
        settings,
      );

      // Merge the saved state back in. Fall back to the locally-built settings if
      // the API returns nothing, so we never replace a group with undefined.
      const merged =
        updated && updated.settings
          ? updated
          : { ...selectedGroup, settings };

      setGroups((prev) =>
        prev.map((g) =>
          g &&
          g.professionType === profession &&
          g.targetRole === targetRole
            ? merged
            : g,
        ),
      );

      setSuccess(
        auto ? "Changes automatically saved." : "Visibility settings saved.",
      );
      refreshMyVisibility();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.response?.data ||
          "Failed to save visibility settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!selectedGroup) return undefined;

    const isDirty = selectedGroup.settings.some(
      (s) => (draft[s.visibilityKey] !== false) !== s.enabled,
    );
    if (!isDirty) return undefined;

    const timer = setTimeout(() => {
      handleSave({ auto: true });
    }, 600);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, selectedGroup]);

  function renderGenericSettings() {
    const settingByKey = new Map(
      selectedGroup.settings.map((s) => [s.visibilityKey, s]),
    );
    const grouped = new Set();

    const sections = LEARNER_SECTIONS.map((sec) => {
      const items = sec.keys.filter((k) => settingByKey.has(k));
      items.forEach((k) => grouped.add(k));
      return { ...sec, items };
    }).filter((sec) => sec.items.length > 0);

    const leftover = selectedGroup.settings.filter(
      (s) => !grouped.has(s.visibilityKey),
    );

    return (
      <div className="space-y-4">
        {sections.map((sec) => (
          <SettingsSection
            key={sec.title}
            icon={sec.icon}
            title={sec.title}
            subtitle={sec.subtitle}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sec.items.map((key) => {
                const setting = settingByKey.get(key);
                return (
                  <CheckRow
                    key={key}
                    label={setting.label}
                    checked={draft[key] !== false}
                    onClick={() => toggle(key)}
                  />
                );
              })}
            </div>
          </SettingsSection>
        ))}

        {leftover.length > 0 && (
          <SettingsSection
            icon="fas fa-sliders"
            title="Other"
            subtitle="Additional settings."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {leftover.map((setting) => (
                <CheckRow
                  key={setting.visibilityKey}
                  label={setting.label}
                  checked={draft[setting.visibilityKey] !== false}
                  onClick={() => toggle(setting.visibilityKey)}
                  hint={setting.visibilityKey}
                />
              ))}
            </div>
          </SettingsSection>
        )}
      </div>
    );
  }

  function renderPracticeInstructorSettings() {
    return (
      <div className="space-y-4">
        {(hasKey("CREATE_EDUCATIONAL_REPORTS") ||
          hasKey("EDIT_OWN_EDUCATIONAL_REPORTS") ||
          hasKey("VIEW_PAST_EDUCATIONAL_REPORTS") ||
          hasKey("VIEW_ALL_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS") ||
          hasKey("VIEW_OWN_CREATED_EDUCATIONAL_REPORTS_ONLY")) && (
          <SettingsSection
            icon="fas fa-file-lines"
            title="Bildungsberichte"
            subtitle="Configure Bildungsbericht access for practice instructors."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {hasKey("CREATE_EDUCATIONAL_REPORTS") && (
                <CheckRow
                  label="Create Bildungsberichte"
                  checked={isOn("CREATE_EDUCATIONAL_REPORTS")}
                  onClick={() => toggle("CREATE_EDUCATIONAL_REPORTS")}
                />
              )}

              {hasKey("EDIT_OWN_EDUCATIONAL_REPORTS") && (
                <CheckRow
                  label="Edit own Bildungsberichte"
                  checked={isOn("EDIT_OWN_EDUCATIONAL_REPORTS")}
                  onClick={() => toggle("EDIT_OWN_EDUCATIONAL_REPORTS")}
                />
              )}

              {hasKey("VIEW_PAST_EDUCATIONAL_REPORTS") && (
                <CheckRow
                  label="Show past Bildungsberichte"
                  checked={isOn("VIEW_PAST_EDUCATIONAL_REPORTS")}
                  onClick={() => toggle("VIEW_PAST_EDUCATIONAL_REPORTS")}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {hasKey("VIEW_ALL_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS") && (
                <RadioRow
                  label="Show all Bildungsberichte of assigned learners"
                  checked={isOn(
                    "VIEW_ALL_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
                  )}
                  disabled={!isOn("VIEW_PAST_EDUCATIONAL_REPORTS")}
                  onClick={() =>
                    chooseExclusive(
                      "VIEW_ALL_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
                      "VIEW_OWN_CREATED_EDUCATIONAL_REPORTS_ONLY",
                    )
                  }
                />
              )}

              {hasKey("VIEW_OWN_CREATED_EDUCATIONAL_REPORTS_ONLY") && (
                <RadioRow
                  label="Show only self-created Bildungsberichte"
                  checked={isOn("VIEW_OWN_CREATED_EDUCATIONAL_REPORTS_ONLY")}
                  disabled={!isOn("VIEW_PAST_EDUCATIONAL_REPORTS")}
                  onClick={() =>
                    chooseExclusive(
                      "VIEW_OWN_CREATED_EDUCATIONAL_REPORTS_ONLY",
                      "VIEW_ALL_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
                    )
                  }
                />
              )}
            </div>
          </SettingsSection>
        )}

        {selectedGroup.settings
          .filter((s) => !GROUPED_PI_KEYS.has(s.visibilityKey))
          .map((s) => (
            <CheckRow
              key={s.visibilityKey}
              label={s.label}
              checked={isOn(s.visibilityKey)}
              onClick={() => toggle(s.visibilityKey)}
              hint={s.visibilityKey}
            />
          ))}

        {(hasKey("VIEW_PAST_SEMESTER_REVIEWS") ||
          hasKey("VIEW_ALL_ASSIGNED_LEARNER_SEMESTER_REVIEWS") ||
          hasKey("VIEW_OWN_OR_INVOLVED_SEMESTER_REVIEWS_ONLY")) && (
          <SettingsSection
            icon="fas fa-clipboard-check"
            title="Semester reviews"
            subtitle="Control which review history practice instructors can see."
          >
            {hasKey("VIEW_PAST_SEMESTER_REVIEWS") && (
              <CheckRow
                label="Show past semester reviews"
                checked={isOn("VIEW_PAST_SEMESTER_REVIEWS")}
                onClick={() => toggle("VIEW_PAST_SEMESTER_REVIEWS")}
              />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {hasKey("VIEW_ALL_ASSIGNED_LEARNER_SEMESTER_REVIEWS") && (
                <RadioRow
                  label="Show all assigned learner reviews"
                  checked={isOn("VIEW_ALL_ASSIGNED_LEARNER_SEMESTER_REVIEWS")}
                  disabled={!isOn("VIEW_PAST_SEMESTER_REVIEWS")}
                  onClick={() =>
                    chooseExclusive(
                      "VIEW_ALL_ASSIGNED_LEARNER_SEMESTER_REVIEWS",
                      "VIEW_OWN_OR_INVOLVED_SEMESTER_REVIEWS_ONLY",
                    )
                  }
                />
              )}

              {hasKey("VIEW_OWN_OR_INVOLVED_SEMESTER_REVIEWS_ONLY") && (
                <RadioRow
                  label="Show only own or involved reviews"
                  checked={isOn("VIEW_OWN_OR_INVOLVED_SEMESTER_REVIEWS_ONLY")}
                  disabled={!isOn("VIEW_PAST_SEMESTER_REVIEWS")}
                  onClick={() =>
                    chooseExclusive(
                      "VIEW_OWN_OR_INVOLVED_SEMESTER_REVIEWS_ONLY",
                      "VIEW_ALL_ASSIGNED_LEARNER_SEMESTER_REVIEWS",
                    )
                  }
                />
              )}
            </div>
          </SettingsSection>
        )}
      </div>
    );
  }

  if (!isAdmin && !isBerufsbildner) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center px-4">
        <div className="card-brand p-8 max-w-lg w-full text-center">
          <div className="w-16 h-16 rounded-2xl bg-sunrise-red/10 text-sunrise-red flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-lock text-xl" />
          </div>
          <h2 className="text-xl font-bold text-grey-dusk mb-2">
            Access denied
          </h2>
          <p className="text-slate-cloud">
            Visibility settings are available for administrators and trainers
            only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card-brand-elevated p-6 mb-6 bg-gradient-to-r from-ice-white to-pale-dawn/30">
          <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-2">
            <i className="fas fa-eye text-sunrise-red mr-4" />
            Visibility Settings
          </h1>
          <p className="text-slate-cloud">
            Configure access per profession and role. Changes are saved
            automatically.
          </p>
        </div>

        {error && (
          <Alert type="error" message={error} onClose={() => setError("")} />
        )}
        {success && (
          <Alert
            type="success"
            message={success}
            onClose={() => setSuccess("")}
          />
        )}

        {loading ? (
          <div className="card-brand p-12 text-center text-slate-cloud">
            <i className="fas fa-spinner fa-spin mr-2 text-sunrise-red" />
            Loading visibility settings...
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <aside className="lg:col-span-4 xl:col-span-3 space-y-4">
              <div className="card-brand p-4">
                <h2 className="text-sm font-bold text-grey-dusk mb-3 flex items-center">
                  <i className="fas fa-sliders text-sunrise-red mr-2" />
                  Configuration
                </h2>

                <label htmlFor="visibility-profession" className="block text-xs font-bold uppercase tracking-wide text-slate-cloud mb-1.5">
                  Profession type
                </label>
                <div className="relative mb-4">
                  <select
                    id="visibility-profession"
                    className="input-brand h-10 appearance-none px-10 py-0 text-center [text-align-last:center] leading-normal cursor-pointer"
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                  >
                    {professions.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <i
                    className="fas fa-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-grey-dusk"
                    aria-hidden="true"
                  />
                </div>

                <label className="block text-xs font-bold uppercase tracking-wide text-slate-cloud mb-1.5">
                  Target role
                </label>
                <div className="space-y-2">
                  {[
                    {
                      value: "LEARNER",
                      label: "Apprentice",
                      icon: "fas fa-user-graduate",
                    },
                    {
                      value: "PRACTICE_INSTRUCTOR",
                      label: "Practice Instructor",
                      icon: "fas fa-user-tie",
                    },
                  ].map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => setTargetRole(role.value)}
                      className={`w-full rounded-xl border p-3 text-left transition-colors flex items-center gap-3 ${
                        targetRole === role.value
                          ? "border-sunrise-red bg-sunrise-red/5"
                          : "border-pale-dawn bg-white hover:bg-ice-white"
                      }`}
                    >
                      <span
                        className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                          targetRole === role.value
                            ? "bg-sunrise-red text-white"
                            : "bg-ice-white text-slate-cloud"
                        }`}
                      >
                        <i className={role.icon} />
                      </span>
                      <span className="text-sm font-semibold text-grey-dusk">
                        {role.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {selectedGroup && (
                <div className="card-brand p-4">
                  <div className="text-xs text-slate-cloud">
                    Enabled features
                  </div>
                  <div className="text-2xl font-bold text-grey-dusk mt-1">
                    {enabledCount}/{selectedGroup.settings.length}
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => setAll(true)}
                      className="h-9 rounded-lg border border-pale-dawn bg-white text-xs font-semibold text-grey-dusk hover:bg-ice-white"
                    >
                      Enable all
                    </button>
                    <button
                      type="button"
                      onClick={() => setAll(false)}
                      className="h-9 rounded-lg border border-pale-dawn bg-white text-xs font-semibold text-grey-dusk hover:bg-ice-white"
                    >
                      Disable all
                    </button>
                  </div>

                  <div className="mt-4 text-xs text-slate-cloud flex items-center gap-2">
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin text-sunrise-red" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check-circle text-green-600" />
                        Auto-save active
                      </>
                    )}
                  </div>
                </div>
              )}
            </aside>

            <main className="lg:col-span-8 xl:col-span-9">
              {selectedGroup ? (
                <div className="card-brand overflow-hidden">
                  <div className="px-5 py-4 border-b border-pale-dawn bg-gradient-to-r from-ice-white to-pale-dawn/30">
                    <h2 className="text-lg font-bold text-grey-dusk flex items-center">
                      <i className="fas fa-list-check text-sunrise-red mr-3" />
                      Feature Visibility
                    </h2>
                    <p className="text-sm text-slate-cloud mt-1">
                      {selectedGroup.professionLabel || profession} ·{" "}
                      {targetRole === "LEARNER"
                        ? "Apprentice"
                        : "Practice Instructor"}
                    </p>
                  </div>

                  <div className="p-5">
                    {targetRole === "PRACTICE_INSTRUCTOR"
                      ? renderPracticeInstructorSettings()
                      : renderGenericSettings()}
                  </div>
                </div>
              ) : (
                <div className="card-brand p-12 text-center text-slate-cloud">
                  No settings for this selection.
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}

// Defined at module scope (not inline in the component) so the identity is stable
// across re-renders — otherwise React remounts every row on each render, which is
// wasteful and detaches DOM nodes mid-update (e.g. during autosave).
function CheckRow({ label, checked, disabled, onClick, hint }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
        disabled
          ? "opacity-50 cursor-not-allowed border-pale-dawn bg-white"
          : checked
            ? "border-green-200 bg-green-50/70 hover:bg-green-50"
            : "border-pale-dawn bg-white hover:bg-ice-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 w-5 h-5 rounded-md grid place-items-center border shrink-0 ${
            checked
              ? "bg-sunrise-red border-sunrise-red text-white"
              : "bg-white border-gray-300 text-transparent"
          }`}
        >
          <i className="fas fa-check text-[11px]" />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-grey-dusk">{label}</div>
        </div>
      </div>
    </button>
  );
}

function RadioRow({ label, checked, disabled, onClick, hint }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
        disabled
          ? "opacity-50 cursor-not-allowed border-pale-dawn bg-white"
          : checked
            ? "border-sunrise-red/40 bg-sunrise-red/5"
            : "border-pale-dawn bg-white hover:bg-ice-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 w-5 h-5 rounded-full grid place-items-center border shrink-0 ${
            checked ? "border-sunrise-red" : "border-gray-300"
          }`}
        >
          {checked && (
            <span className="w-2.5 h-2.5 rounded-full bg-sunrise-red" />
          )}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-grey-dusk">{label}</div>
          {hint && <div className="text-xs text-slate-cloud mt-0.5">{hint}</div>}
        </div>
      </div>
    </button>
  );
}

// Semantic grouping for the LEARNER visibility keys, mirroring the sectioned
// Practice-Instructor layout. Keys not listed here fall into a generic "Weitere"
// group, so the page never silently drops a key.
const LEARNER_SECTIONS = [
  {
    icon: "fas fa-graduation-cap",
    title: "Grades",
    subtitle: "The learner's access to their grades.",
    keys: ["VIEW_GRADES", "CREATE_GRADES", "EDIT_OWN_GRADES"],
  },
  {
    icon: "fas fa-clipboard-check",
    title: "Semester reviews",
    subtitle: "Visibility of semester reviews.",
    keys: ["VIEW_SEMESTER_REVIEWS"],
  },
  {
    icon: "fas fa-file-lines",
    title: "Bildungsberichte",
    subtitle: "Visibility of educational reports.",
    keys: ["VIEW_EDUCATIONAL_REPORTS"],
  },
  {
    icon: "fas fa-book",
    title: "Learning documentation",
    subtitle: "Access to the learning documentation.",
    keys: ["VIEW_LEARNING_DOCUMENTATION", "EDIT_LEARNING_DOCUMENTATION_LINK"],
  },
  {
    icon: "fas fa-coins",
    title: "Bonus",
    subtitle: "Visibility of the bonus overview.",
    keys: ["VIEW_BONUS"],
  },
];

function SettingsSection({ icon, title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-pale-dawn bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-pale-dawn bg-ice-white/70">
        <h3 className="text-sm font-bold text-grey-dusk flex items-center">
          <i className={`${icon} text-sunrise-red mr-2`} />
          {title}
        </h3>
        {subtitle && (
          <p className="text-xs text-slate-cloud mt-1">{subtitle}</p>
        )}
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </section>
  );
}

function Alert({ type, message, onClose }) {
  const isError = type === "error";
  return (
    <div
      className={`mb-4 rounded-xl border px-4 py-3 text-sm flex items-center justify-between ${
        isError
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-green-200 bg-green-50 text-green-700"
      }`}
    >
      <span>
        <i
          className={`fas ${isError ? "fa-exclamation-circle" : "fa-check-circle"} mr-2`}
        />
        {message}
      </span>
      <button onClick={onClose}>
        <i className="fas fa-times" />
      </button>
    </div>
  );
}

const GROUPED_PI_KEYS = new Set([
  "VIEW_PAST_SEMESTER_REVIEWS",
  "VIEW_ALL_ASSIGNED_LEARNER_SEMESTER_REVIEWS",
  "VIEW_OWN_OR_INVOLVED_SEMESTER_REVIEWS_ONLY",
  "CREATE_EDUCATIONAL_REPORTS",
  "EDIT_OWN_EDUCATIONAL_REPORTS",
  "VIEW_PAST_EDUCATIONAL_REPORTS",
  "VIEW_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
  "VIEW_ALL_ASSIGNED_LEARNER_EDUCATIONAL_REPORTS",
  "VIEW_OWN_CREATED_EDUCATIONAL_REPORTS_ONLY",
]);

function normalize(role) {
  const raw = typeof role === "string" ? role : role?.name || role?.authority;
  if (!raw || typeof raw !== "string") return "";
  return raw.toUpperCase().replace(/^ROLE_/, "");
}

export default VisibilitySettingsPage;
